import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AsyncNotification,
  Button,
  GoogleFontIcon,
} from '@asset-metrix/portal-ui';
import { useDisclosure } from '@mantine/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { v4 as uuidV4 } from 'uuid';

import { HttpError, isNetworkError } from '../../api/errors';
import {
  dashboardQueryKey,
  deleteDashboard,
  invalidateDashboardListQueries,
  invalidateDashboardQuery,
  invalidatePageDashboardsQuery,
  updateDashboard,
} from '../../api/portalGateway/dashboard';
import { applyDashboardNavCacheChange } from '../../api/portalGateway/navigation';
import {
  deleteFilterCustomization,
  mutateFilterCustomization,
} from '../../api/portalGateway/tableFilterCustomization';
import {
  projectDashboardUpdateRequest,
  useDashboardEditModeStore,
  useIsDashboardDraftDirty,
  useIsDashboardDraftMode,
} from '../../store/states/dashboardEditMode';
import { isNull } from '../../utils/nullish';

import type {
  DashboardSharingStatus,
  HydratedDashboard,
} from './utils/dashboard.types';
import { getDashboardSharingOption } from './utils/dashboardSharing';
import {
  type AddComponentSelection,
  AddComponentToDashboardModal,
} from './AddComponentToDashboardModal';
import { DashboardDeleteConfirmation } from './DashboardDeleteConfirmation';
import {
  planDraftCustomFilterWrites,
  rollbackDraftCustomFilterUpserts,
  snapshotFilterCustomizationCache,
  writeDraftCustomFilterCache,
} from './dashboardDraftCustomFilters';
import { DashboardUnsavedChangesModal } from './DashboardUnsavedChangesModal';
import { ShareDashboardModal } from './ShareDashboardModal';

import pageHeaderActionsStyles from '../page/pageHeaderActions.module.scss';

type DashboardEditActionsProps = {
  dashboardId: string;
  hydratedDashboard: HydratedDashboard;
  isOwner: boolean;
};

function isAmbiguousSaveFailure(error: unknown): boolean {
  if (isNetworkError(error)) {
    return true;
  }
  if (!(error instanceof HttpError)) {
    return false;
  }
  // Gateway/proxy timeouts and bad gateway — request may have reached poco-api.
  return [0, 502, 504].includes(error.statusCode);
}

export function DashboardEditActions({
  dashboardId,
  hydratedDashboard,
  isOwner,
}: DashboardEditActionsProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isDraftMode = useIsDashboardDraftMode(dashboardId);
  const isDirty = useIsDashboardDraftDirty(dashboardId);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [
    addComponentsOpened,
    { close: closeAddComponents, open: openAddComponents },
  ] = useDisclosure(false);
  const [shareOpened, { close: closeShare, open: openShare }] =
    useDisclosure(false);
  const draftContents = useDashboardEditModeStore(
    (state) => state.draftContents
  );
  const draftMetadata = useDashboardEditModeStore(
    (state) => state.draftMetadata
  );
  const isSaving = useDashboardEditModeStore((state) => state.isSaving);
  const filtersLoading = useDashboardEditModeStore(
    (state) => Object.keys(state.tileFiltersPendingCounts).length > 0
  );
  const enterDashboardEdit = useDashboardEditModeStore(
    (state) => state.enterDashboardEdit
  );
  const exitDashboardEdit = useDashboardEditModeStore(
    (state) => state.exitDashboardEdit
  );
  const appendPlacement = useDashboardEditModeStore(
    (state) => state.appendPlacement
  );
  const setIsSaving = useDashboardEditModeStore((state) => state.setIsSaving);
  const pendingDashboardModal = useDashboardEditModeStore(
    (state) => state.pendingDashboardModal
  );
  const clearPendingDashboardModal = useDashboardEditModeStore(
    (state) => state.clearPendingDashboardModal
  );

  // Empty-dashboard Add Component CTA sets pendingDashboardModal; this effect
  // opens the modal. The container only mounts this component for owners.
  useEffect(() => {
    if (pendingDashboardModal !== 'addComponents') {
      return;
    }
    openAddComponents();
    clearPendingDashboardModal();
  }, [clearPendingDashboardModal, openAddComponents, pendingDashboardModal]);

  if (!isOwner) {
    return null;
  }

  const onCancel = () => {
    if (isDirty) {
      setCancelModalOpen(true);
      return;
    }
    exitDashboardEdit();
  };

  const onAddComponents = (contents: AddComponentSelection[]) => {
    for (const item of contents) {
      appendPlacement({
        instanceId: uuidV4(),
        widthFactor: item.widthFactor,
        content: item.content,
        sourcePageId: item.sourcePageId,
      });
    }
  };

  const onSave = async () => {
    if (isNull(draftContents) || isNull(draftMetadata)) {
      return;
    }
    const draftFilterWrites = planDraftCustomFilterWrites(draftContents);
    const baselineInstanceIds = new Set(
      useDashboardEditModeStore
        .getState()
        .baselineForDirtyCheck?.contents.map((tile) => tile.instanceId)
    );
    const priorFilterCustomizations = snapshotFilterCustomizationCache(
      queryClient,
      draftFilterWrites.upserts
    );
    setIsSaving(true);
    // Persist draft filter lists before the dashboard update. The query cache
    // is updated only after both succeed, so leaving edit cannot drop the only
    // copy of those lists. A rejected dashboard update restores prior lists for
    // saved tiles and deletes lists created for new tiles; an ambiguous
    // dashboard failure keeps the written lists. An empty draft list is deleted
    // only after the dashboard update succeeds, so a failed save cannot drop a
    // list the tile still shows.
    let filtersWritten = false;
    let dashboardUpdated = false;
    try {
      await Promise.all(
        draftFilterWrites.upserts.map((item) => mutateFilterCustomization(item))
      );
      filtersWritten = true;
      const body = projectDashboardUpdateRequest(draftMetadata, draftContents);
      const updated = await updateDashboard({ dashboardId, data: body });
      dashboardUpdated = true;
      queryClient.setQueryData(dashboardQueryKey(dashboardId), updated);
      writeDraftCustomFilterCache(queryClient, {
        upserts: draftFilterWrites.upserts,
        deletes: [],
      });
      await Promise.all(
        draftFilterWrites.deletes.map((item) => deleteFilterCustomization(item))
      );
      writeDraftCustomFilterCache(queryClient, {
        upserts: [],
        deletes: draftFilterWrites.deletes,
      });
      exitDashboardEdit();
    } catch (error) {
      const dashboardOutcomeUnknown =
        filtersWritten && !dashboardUpdated && isAmbiguousSaveFailure(error);
      if (!dashboardUpdated && !dashboardOutcomeUnknown) {
        await rollbackDraftCustomFilterUpserts({
          upserts: draftFilterWrites.upserts,
          baselineInstanceIds,
          priors: priorFilterCustomizations,
        });
      }
      if (dashboardOutcomeUnknown) {
        await invalidateDashboardQuery(queryClient, dashboardId);
        AsyncNotification.show(
          { message: t('app.dashboard.editAction.saveOutcomeUnknown') },
          'error'
        );
        return;
      }
      const message =
        error instanceof HttpError
          ? error.message
          : t('app.dashboard.editAction.saveFailed');
      AsyncNotification.show({ message }, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const onDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await deleteDashboard({
        dashboardId,
        sourcePageId: hydratedDashboard.sourcePageId,
      });
      setDeleteOpen(false);
      const sourcePageId = hydratedDashboard.sourcePageId;
      applyDashboardNavCacheChange(queryClient, sourcePageId, {
        op: 'remove',
        dashboardId,
      });
      await invalidateDashboardListQueries(queryClient, sourcePageId);
      AsyncNotification.show(
        { message: t('app.dashboard.deleteConfirmation.deleteSuccess') },
        'success'
      );
    } catch (error) {
      const message =
        error instanceof HttpError
          ? error.message
          : t('app.dashboard.deleteConfirmation.deleteFailed');
      AsyncNotification.show({ message }, 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const onShareSave = async (sharingStatus: DashboardSharingStatus) => {
    setIsSharing(true);
    try {
      const body = projectDashboardUpdateRequest(
        { ...hydratedDashboard, sharingStatus },
        hydratedDashboard.contents
      );
      const updated = await updateDashboard({ dashboardId, data: body });
      queryClient.setQueryData(dashboardQueryKey(dashboardId), updated);
      await invalidatePageDashboardsQuery(
        queryClient,
        hydratedDashboard.sourcePageId
      );
      closeShare();
      AsyncNotification.show(
        { message: t('app.dashboard.shareModal.submitSuccess') },
        'success'
      );
    } catch (error) {
      const message =
        error instanceof HttpError
          ? error.message
          : t('app.dashboard.shareModal.submitFailed');
      AsyncNotification.show({ message }, 'error');
    } finally {
      setIsSharing(false);
    }
  };

  const draftModeActions = [
    {
      title: 'app.page.editAction.label.addComponent',
      icon: <GoogleFontIcon name="add" />,
      onClick: openAddComponents,
      disabled: isSaving,
    },
    {
      title: 'app.dashboard.editAction.label.cancel',
      icon: <GoogleFontIcon name="close" />,
      onClick: onCancel,
      disabled: isSaving,
    },
    {
      title: 'app.page.editAction.label.save',
      icon: <GoogleFontIcon name="save" />,
      onClick: () => void onSave(),
      loading: isSaving,
      disabled: isSaving || filtersLoading,
    },
  ] as const;

  const sharingOption = getDashboardSharingOption(
    hydratedDashboard.sharingStatus
  );
  const viewModeActions = [
    {
      title: 'app.page.editAction.label.delete',
      icon: <GoogleFontIcon name="delete" />,
      onClick: () => setDeleteOpen(true),
      disabled: isDeleting || isSharing,
    },
    {
      title: sharingOption.labelKey,
      icon: <GoogleFontIcon name={sharingOption.iconName} />,
      onClick: openShare,
      disabled: isDeleting || isSharing,
    },
    {
      title: 'app.dashboard.editAction.label.editLayout',
      icon: <GoogleFontIcon name="edit" />,
      onClick: () => enterDashboardEdit(dashboardId, hydratedDashboard),
      disabled: isDeleting || isSharing,
    },
  ] as const;

  const actions = isDraftMode ? draftModeActions : viewModeActions;

  return (
    <>
      <div className={pageHeaderActionsStyles['actions']}>
        {actions.map(({ title, ...config }) => (
          <Button key={title} variant="secondary" title={t(title)} {...config}>
            {t(title)}
          </Button>
        ))}
      </div>
      <AddComponentToDashboardModal
        onAddComponents={onAddComponents}
        onClose={closeAddComponents}
        opened={addComponentsOpened}
      />
      <DashboardUnsavedChangesModal
        opened={cancelModalOpen}
        onStay={() => setCancelModalOpen(false)}
        onDiscard={() => {
          setCancelModalOpen(false);
          exitDashboardEdit();
        }}
      />
      <DashboardDeleteConfirmation
        opened={deleteOpen}
        dashboardName={hydratedDashboard.name}
        loading={isDeleting}
        warningMessage={t('app.dashboard.deleteConfirmation.permanentWarning')}
        onCancel={() => {
          if (!isDeleting) {
            setDeleteOpen(false);
          }
        }}
        onConfirm={() => void onDeleteConfirm()}
      />
      <ShareDashboardModal
        opened={shareOpened}
        sharingStatus={hydratedDashboard.sharingStatus}
        loading={isSharing}
        onClose={() => {
          if (!isSharing) {
            closeShare();
          }
        }}
        onSave={(status) => void onShareSave(status)}
      />
    </>
  );
}
